import React, { type ReactNode } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import Button from 'components/common/button';
import Loader from 'components/common/loader';
import { FeatureFlag } from 'constants/feature-flags';
import { links } from 'constants/external-links';
import { typeStyle } from 'constants/typography';
import { useFeatureFlag, useFeatureFlags } from 'hooks/use-feature-flags';
import useStores from 'hooks/use-stores.hooks';

export const SignupsPaused: React.FC = () => (
  <Container data-e2e="signups-paused">
    <Title>
      <FormattedMessage id="onboarding.signups-paused.title" />
    </Title>
    <Description>
      <FormattedMessage id="onboarding.signups-paused.description" />
    </Description>
    <Button data-e2e="signups-paused-request-access" type="primary" href={links.REQUEST_ACCESS} isExternalLink>
      <FormattedMessage id="onboarding.signups-paused.request-access" />
    </Button>
  </Container>
);

export const SignupsPausedGate: React.FC<{ children?: ReactNode }> = observer(function SignupsPausedGate({ children }) {
  const { isInitialized } = useFeatureFlags();
  const areNewSignupsPaused = useFeatureFlag(FeatureFlag.PAUSE_NEW_SIGNUPS);
  const { organizationsStore } = useStores();
  const isNewSignup = organizationsStore.isDoneFetchingOrganizations && isEmpty(organizationsStore.organizations);

  if (!isInitialized) return <Loader size="large" />;
  return areNewSignupsPaused && isNewSignup ? <SignupsPaused /> : <>{children}</>;
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-lg);
  max-width: 560px;
  margin: auto;
  padding: var(--spacing-lg);
`;

const Title = styled.div`
  ${typeStyle.headingH1}
  color: var(--text-heading);
`;

const Description = styled.div`
  ${typeStyle.bodyLg}
  color: var(--text-secondary);
`;
